import Icon from "../../../../components/ui/Icon";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller, FieldError } from "react-hook-form";
import {
  AddIcon,
  AsyncSelect,
  Button,
  FlexContent,
  Form,
  FormField,
  ModalDialog,
  Numeric,
  TableCell,
  TableHeadCell,
  TableRow,
} from "@rothoblaas/design-system";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import Table from "../../../../components/ui/Table";
import { getDeleteAction, getEditAction } from "../../../../utils/rowActions";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useInitializer from "../../../../hooks/useInitializer";
import useListFilters from "../../../../hooks/useListFilters";
import useI18n from "../../../../hooks/useTranslations";
import { AdminLockPairLockstopMapping } from "../../../../http/lock_calculator";
import { useAdminLockCalculatorApi } from "../../../../http/lock_calculator/useAdminLockCalculator";
import { PaginationQuery } from "../../../../types";

type SelectOption = { value: string; label: string };

interface FormBody {
  lockA: SelectOption | null;
  lockB: SelectOption | null;
  lockstop: SelectOption | null;
  quantity: number | null;
  installationLevels: number | null;
}

/**
 * Compatibilità dei lockstop con DUE connettori affiancati (la coppia è non
 * ordinata: A+B e B+A sono la stessa riga). Con 2 connettori e LOCK STOP il
 * generatore, il wizard e la distinta usano solo queste righe: le compatibilità
 * per singolo lock non valgono per le coppie.
 */
const AdminLockPairLockstopMappingsTab: FC = () => {
  const i18n = useI18n();
  const labels = i18n.translation.lockCalculatorModule.admin.lockPairLockstopMappings;
  const adminApi = useAdminLockCalculatorApi();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ isSearchable: true, prefix: "lockPairLockstop" });
  const aborter = useRef(new AbortController());
  const query: PaginationQuery = { ...filters.state, q: filters.state.q };
  const { data, error } = adminApi.useLockPairLockstopMappings(query, aborter.current.signal);
  const isInitialized = useInitializer(data || error);

  const [itemToDelete, setItemToDelete] = useState<AdminLockPairLockstopMapping>();
  const [itemToEdit, setItemToEdit] = useState<AdminLockPairLockstopMapping>();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!data && !error && !isInitialized) return null;

  return (
    <>
      <FlexContent padding>
        <Table<AdminLockPairLockstopMapping>
          filters={filters}
          search={{
            placeholder: i18n.translation.common.search + "...",
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          headingActions={
            <Button onClick={() => setIsCreateOpen(true)}>
              <Icon glyph={AddIcon} />
              {i18n.translation.common.add}
            </Button>
          }
          head={
            <TableRow>
              <TableHeadCell align="left">{labels.lockA}</TableHeadCell>
              <TableHeadCell align="left">{labels.lockB}</TableHeadCell>
              <TableHeadCell align="left">{labels.lockstop}</TableHeadCell>
              <TableHeadCell align="left">{labels.quantity}</TableHeadCell>
              <TableHeadCell align="left">{labels.installationLevels}</TableHeadCell>
              <TableHeadCell align="left"></TableHeadCell>
            </TableRow>
          }
          renderRow={(item) => (
            <>
              <TableCell>{item.lockA.code}</TableCell>
              <TableCell>{item.lockB.code}</TableCell>
              <TableCell>{item.lockstop.code}</TableCell>
              <TableCell>{item.quantity}</TableCell>
              <TableCell>{item.installationLevels}</TableCell>
            </>
          )}
          onRowClick={(item) => setItemToEdit(item)}
          rowActions={(item) => [
            getEditAction(i18n, () => setItemToEdit(item)),
            getDeleteAction(i18n, () => setItemToDelete(item), true),
          ]}
          items={data?.items}
          total={data?.total}
          error={!!error}
        />

        {!!itemToEdit && <FormModal item={itemToEdit} onClose={() => setItemToEdit(undefined)} />}

        {!!itemToDelete && (
          <ModalDialog
            onClose={() => setItemToDelete(undefined)}
            title={labels.singular}
            isSubmitting={isSubmitting}
            buttons={[
              <Button key={0} onClick={() => setItemToDelete(undefined)} variant="secondary">
                {i18n.translation.common.cancel}
              </Button>,
              <Button
                key={1}
                onClick={() => {
                  setIsSubmitting(true);
                  adminApi
                    .deleteLockPairLockstopMapping(itemToDelete.lockA.id, itemToDelete.lockB.id, itemToDelete.lockstop.id)
                    .then(() => {
                      dispatch(showSuccessNotification(i18n.translation));
                      setItemToDelete(undefined);
                    })
                    .catch((err) => dispatch(showErrorNotification(i18n, err)))
                    .finally(() => setIsSubmitting(false));
                }}
              >
                {i18n.translation.common.yes}
              </Button>,
            ]}
          >
            {i18n.translation.clients.questions.delete}
          </ModalDialog>
        )}
      </FlexContent>

      {isCreateOpen && <FormModal onClose={() => setIsCreateOpen(false)} />}
    </>
  );
};

/** Select asincrona su un catalogo (lock o lockstop) per un campo del form. */
const ItemSelect: FC<{
  label: string;
  disabled: boolean;
  value: SelectOption | null;
  invalid: boolean;
  error: FieldError | undefined;
  defaultOptions: SelectOption[];
  load: (q: string) => Promise<SelectOption[]>;
  onChange: (option: SelectOption | null) => void;
}> = ({ label, disabled, value, invalid, error, defaultOptions, load, onChange }) => {
  const i18n = useI18n();
  return (
    <FormField label={label} error={error}>
      {({ labelId }) => (
        <AsyncSelect<SelectOption, false>
          inputId={labelId}
          cacheOptions
          defaultOptions
          loadOptions={load}
          isMulti={false}
          isDisabled={disabled}
          placeholder={`${i18n.translation.common.select}...`}
          getOptionLabel={(x) => x.label}
          getOptionValue={(x) => x.value}
          isInvalid={invalid}
          onChange={(option) => onChange(option)}
          value={defaultOptions.find((o) => o.value === value?.value) ?? value}
        />
      )}
    </FormField>
  );
};

const FormModal: FC<{ item?: AdminLockPairLockstopMapping; onClose: () => void }> = ({ item, onClose }) => {
  const i18n = useI18n();
  const labels = i18n.translation.lockCalculatorModule.admin.lockPairLockstopMappings;
  const dispatch = useContext(NotificationDispatch);
  const adminApi = useAdminLockCalculatorApi();
  const [defaultLockOptions, setDefaultLockOptions] = useState<SelectOption[]>([]);
  const [defaultLockstopOptions, setDefaultLockstopOptions] = useState<SelectOption[]>([]);

  useEffect(() => {
    adminApi
      .getLocks({ q: "", page: 1, limit: 100 }, new AbortController().signal)
      .then((items) => setDefaultLockOptions(items.map((x) => ({ value: x.item.id, label: x.item.code }))))
      .catch(() => setDefaultLockOptions([]));
    adminApi
      .getLockStops({ q: "", page: 1, limit: 100 }, new AbortController().signal)
      .then((items) => setDefaultLockstopOptions(items.map((x) => ({ value: x.item.id, label: x.item.code }))))
      .catch(() => setDefaultLockstopOptions([]));
  }, []);

  const loadLocks = (q: string) =>
    adminApi
      .getLocks({ q: q || "", page: 1, limit: 20 }, new AbortController().signal)
      .then((items) => items.map((x) => ({ value: x.item.id, label: x.item.code })));
  const loadLockstops = (q: string) =>
    adminApi
      .getLockStops({ q: q || "", page: 1, limit: 20 }, new AbortController().signal)
      .then((items) => items.map((x) => ({ value: x.item.id, label: x.item.code })));

  const defaultValues: FormBody = {
    lockA: item ? { value: item.lockA.id, label: item.lockA.code } : null,
    lockB: item ? { value: item.lockB.id, label: item.lockB.code } : null,
    lockstop: item ? { value: item.lockstop.id, label: item.lockstop.code } : null,
    quantity: item?.quantity ?? null,
    installationLevels: item?.installationLevels ?? null,
  };

  const onSubmit = () => async (formData: FormBody) => {
    if (
      !formData.lockA?.value ||
      !formData.lockB?.value ||
      !formData.lockstop?.value ||
      formData.quantity == null ||
      formData.installationLevels == null
    )
      return;
    try {
      if (item) {
        await adminApi.updateLockPairLockstopMapping(item.lockA.id, item.lockB.id, item.lockstop.id, {
          quantity: formData.quantity,
          installationLevels: formData.installationLevels,
        });
      } else {
        await adminApi.insertLockPairLockstopMapping({
          lockAItemId: formData.lockA.value,
          lockBItemId: formData.lockB.value,
          lockstopItemId: formData.lockstop.value,
          quantity: formData.quantity,
          installationLevels: formData.installationLevels,
        });
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={item ? `${i18n.translation.common.edit} ${labels.singular}` : `${i18n.translation.common.add} ${labels.singular}`}
    >
      <Form<FormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
        secondaryButton={
          <Button onClick={onClose} variant="secondary">
            {i18n.translation.common.cancel}
          </Button>
        }
      >
        {({ control }) => (
          <FormFieldsContainer fullWidth>
            <Controller
              name="lockA"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <ItemSelect
                  label={labels.lockA}
                  disabled={!!item}
                  value={field.value}
                  invalid={fieldState.invalid}
                  error={fieldState.error}
                  defaultOptions={defaultLockOptions}
                  load={loadLocks}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              name="lockB"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <ItemSelect
                  label={labels.lockB}
                  disabled={!!item}
                  value={field.value}
                  invalid={fieldState.invalid}
                  error={fieldState.error}
                  defaultOptions={defaultLockOptions}
                  load={loadLocks}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              name="lockstop"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <ItemSelect
                  label={labels.lockstop}
                  disabled={!!item}
                  value={field.value}
                  invalid={fieldState.invalid}
                  error={fieldState.error}
                  defaultOptions={defaultLockstopOptions}
                  load={loadLockstops}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              name="quantity"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={labels.quantity} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={0}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="installationLevels"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={labels.installationLevels} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={0}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default AdminLockPairLockstopMappingsTab;
